import React from "react";
import { useLang } from "../LangContext";
import { IMAGES } from "../i18n";
import { motion } from "framer-motion";
import { ArrowDown } from "lucide-react";

export default function Hero() {
    const { t } = useLang();

    return (
        <section
            data-testid="hero-section"
            className="relative min-h-screen flex flex-col justify-end overflow-hidden"
        >
            {/* Background image + gradient overlays */}
            <div
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${IMAGES.hero})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/65 to-ink" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink via-transparent to-transparent opacity-80" />

            {/* Content */}
            <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 pt-40 w-full">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, ease: "easeOut" }}
                    className="max-w-4xl"
                >
                    <span
                        data-testid="hero-kicker"
                        className="inline-block text-xs font-mono uppercase tracking-[0.3em] text-volt border border-volt/40 px-3 py-1.5 mb-8"
                    >
                        {t.hero.kicker}
                    </span>
                    <h1
                        data-testid="hero-title"
                        className="font-graffiti text-6xl sm:text-7xl lg:text-9xl leading-[0.85] tracking-tight text-white"
                    >
                        {t.hero.title}{" "}
                        <span className="text-volt">{t.hero.titleAccent}</span>
                    </h1>
                    <p
                        data-testid="hero-subtitle"
                        className="mt-8 max-w-2xl text-lg md:text-xl text-zinc-300 leading-relaxed"
                    >
                        {t.hero.subtitle}
                    </p>
                    <div className="mt-10 flex flex-wrap items-center gap-4">
                        <a
                            href="#donate"
                            data-testid="hero-donate-btn"
                            className="btn-volt px-8 py-4 text-sm"
                        >
                            {t.hero.cta_primary} →
                        </a>
                        <a
                            href="#program"
                            data-testid="hero-program-btn"
                            className="btn-ghost px-8 py-4 text-sm uppercase font-bold tracking-wider"
                        >
                            {t.hero.cta_secondary}
                        </a>
                    </div>
                </motion.div>

                {/* Stats strip */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5, duration: 0.8 }}
                    className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10"
                >
                    {[t.hero.stat1, t.hero.stat2, t.hero.stat3].map((s, i) => (
                        <div
                            key={i}
                            data-testid={`hero-stat-${i}`}
                            className="bg-ink p-8 md:p-10"
                        >
                            <div className="font-display font-black text-5xl md:text-6xl text-volt leading-none">
                                {s.value}
                            </div>
                            <div className="mt-3 text-sm text-zinc-400 uppercase tracking-wider">
                                {s.label}
                            </div>
                        </div>
                    ))}
                </motion.div>
            </div>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 hidden md:flex">
                <ArrowDown size={20} className="text-zinc-500 animate-bounce" />
            </div>
        </section>
    );
}
